import type { GetLandingPageMappingsQuery } from '../lib/graphql/generated/graphql'
import type { LandingpageMappingRow } from '../lib/referer/matchLandingpageMapping'
import type { RefIdQuery } from './refIdQuery'
import { hasEffectiveRefId } from './refIdQuery'

/** Fallback when `refID` is set but does not match Strapi. */
export const PARENTS_BOOKING_URL_FALLBACK =
    'https://www.spkhb.de/de/home/kontakt-neo/terminvereinbarung-altersvorsorge.html?n=true&stref=opener'

export type ParentsBranchPickerLink = {
    documentId: string
    name: string
    url: string
}

export type ParentsAppointmentOutcome =
    | { kind: 'singleUrl'; url: string }
    | { kind: 'branchPicker' }

export function landingMappingRowsFromQuery(
    data: GetLandingPageMappingsQuery | null | undefined,
): LandingpageMappingRow[] {
    return (data?.landingpageMappings ?? []).filter(
        (r): r is LandingpageMappingRow => r != null,
    )
}

export function findLandingMappingByDocumentId(
    documentId: string,
    rows: LandingpageMappingRow[],
): LandingpageMappingRow | null {
    const id = documentId.trim()
    if (!id) {
        return null
    }
    return rows.find((r) => r.documentId === id) ?? null
}

/**
 * Rows with non-empty `Name` and `Terminbuchung`, sorted by label for the branch picker.
 */
export function buildParentsBranchPickerLinks(
    rows: LandingpageMappingRow[],
): ParentsBranchPickerLink[] {
    return rows
        .filter((r) => Boolean(r.Terminbuchung?.trim()) && Boolean(r.Name?.trim()))
        .map((r) => ({
            documentId: r.documentId,
            name: r.Name.trim(),
            url: r.Terminbuchung.trim(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'))
}

/**
 * How the parents “Termin” CTA should behave: one URL, or branch-picker modal when
 * there is no effective `refID` and no `entryReferrerLandingMatch` mapping.
 */
export function resolveParentsAppointmentOutcome(args: {
    refIdQuery: RefIdQuery
    mappingRows: LandingpageMappingRow[]
    referrerMatchMapping: LandingpageMappingRow | null | undefined
    fallbackUrl?: string
}): ParentsAppointmentOutcome {
    const fallback = args.fallbackUrl ?? PARENTS_BOOKING_URL_FALLBACK

    if (hasEffectiveRefId(args.refIdQuery)) {
        const ref = args.refIdQuery.value.trim()
        const row = findLandingMappingByDocumentId(ref, args.mappingRows)
        const termin = row?.Terminbuchung?.trim()
        if (termin) {
            return { kind: 'singleUrl', url: termin }
        }
        return { kind: 'singleUrl', url: fallback }
    }

    const referrerTermin = args.referrerMatchMapping?.Terminbuchung?.trim()
    if (referrerTermin) {
        return { kind: 'singleUrl', url: referrerTermin }
    }

    return { kind: 'branchPicker' }
}
